import { useCallback, useEffect, useState } from "react";
import { SLIDES } from "@/data/growthOSData";

/**
 * Drives the progressive slide reveal. Exposes index, current slide, navigation
 * handlers, pause state, and wires up keyboard shortcuts:
 *   →, Space, n  -> next
 *   ←, p         -> prev
 *   1-6          -> jump to that slide
 *   .            -> toggle pause (freeze ambient motion)
 */
export default function usePresentation(initialIndex = 0) {
  const [index, setIndex] = useState(initialIndex);
  const [paused, setPaused] = useState(false);

  const total = SLIDES.length;
  const slide = SLIDES[index];

  const next = useCallback(() => {
    setIndex((i) => Math.min(i + 1, total - 1));
  }, [total]);
  const prev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);
  const goTo = useCallback(
    (i) => {
      if (i >= 0 && i < total) setIndex(i);
    },
    [total],
  );
  const togglePause = useCallback(() => setPaused((p) => !p), []);

  useEffect(() => {
    const onKey = (e) => {
      // ignore when typing in inputs
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;

      if (e.key === "ArrowRight" || e.key === " " || e.key === "n") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft" || e.key === "p") {
        e.preventDefault();
        prev();
      } else if (/^[1-9]$/.test(e.key)) {
        const target = parseInt(e.key, 10) - 1;
        if (target < total) goTo(target);
      } else if (e.key === ".") {
        togglePause();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, goTo, togglePause, total]);

  return {
    index,
    slide,
    total,
    next,
    prev,
    goTo,
    paused,
    togglePause,
    isFirst: index === 0,
    isLast: index === total - 1,
  };
}
